/**
 * Capability-Honest Preflight 契约（REQ-40）——主会话启动前的五面能力核验结果。
 *
 * 业务背景（大白话）：员工点了发送，才发现模型没授权 / 连接器没挂上 / 权限模式被顶替。
 * 这些事实在发送前其实**都已经知道了**，只是没人在那一刻把它讲给用户听——错误要么在轮次
 * 中间以协议文案砸出来，要么干脆只写进 console.warn，员工全程无感。preflight 就是在发送前
 * 把这五面已知事实一次性摊开。跨族切模型会按摘要把历史带过去，不再作为拦发理由。
 *
 * 本模块是**纯数据契约 + 纯聚合器 + 运行时守卫**：不含探测逻辑、不读文件、不发请求、
 * 不引用 SessionConfig。谁去读哪个既有状态源，是 core 侧 `session-preflight.ts` 的事
 * （红线 #9：preflight 是聚合器不是探测器，五面判定源全部复用既有的那一份）。
 *
 * 与仓内另外两套 readiness 词汇的边界（刻意不互相吞并）：
 *   - `executor-contract.ts` 的 `ExecutorReadiness` —— 一个 bounded CLI 子进程（codex 等）
 *     能不能被代跑（安装/CLI 登录/Model Wallet）。归 board-03 EXE-02，与本模块零重叠。
 *   - `backend/types.ts` 的 `BackendPreflight*` —— headless required turn 的后端内探测
 *     （真去 connect + listTools）。本模块站在它**旁边**：主会话面、纯内存聚合、不探测。
 */
export const SESSION_PREFLIGHT_CONTRACT_VERSION = 1;
/**
 * 五面。顺序即展示顺序，也是 `SessionPreflightReport.faces` 的固定顺序（守卫会校验）。
 *   - `backend`    ① 后端腿就绪（pi / Claude SDK 后端实例可用）
 *   - `model`      ② 目标模型可路由且已授权
 *   - `tools`      ③ 声明为**必需**的工具 / MCP server 实际挂载
 *   - `permission` ④ 生效的权限模式与这个会话自己存的设定一致
 *   - `history`    ⑤ 恢复场景下历史真的可以继续
 */
export const SESSION_PREFLIGHT_FACES = [
    "backend",
    "model",
    "tools",
    "permission",
    "history",
];
/**
 * 三态是硬需求，不是布尔（仓内 `ExecutorReadiness` / `CapabilityReadinessStatusV1` /
 * `protocolAvailability` 已各自独立收敛到同一结论）：
 *   - `pass`    这一面已确认没问题
 *   - `fail`    这一面已确认有问题 → fail-closed，阻断发送
 *   - `unknown` 这一面**这一刻答不出来**（判定源尚未就绪 / 快照不可用）→ 不阻断，但要展示
 *
 * 「查不到」绝不等于「不可用」——塌成布尔就会重犯那四个模块都修过的 bug。
 */
export const SESSION_PREFLIGHT_STATUS_VALUES = ["pass", "fail", "unknown"];
/**
 * 稳定原因码。**是机器码不是散文**——用户看到的文案由 UI 按码取 locale
 * （`chat.preflight.reason.<code>`），所以这里绝不放已翻译的句子：core 不做 i18n。
 */
export const SESSION_PREFLIGHT_REASONS = [
    // ① backend
    /** 会话已被停止（Stop / 重建中途），这条腿不能再发。 */
    "backend_stopped",
    // ② model
    /** 会话没解析出目标模型 —— 会用后端自己的默认模型跑，所以无从判断它可不可路由。 */
    "model_unresolved",
    /** 目标模型不在这台机器当前可路由的凭证集合内。 */
    "model_not_routable",
    /** 可路由集合快照本身取不到（catalog / 凭证读取失败时的 fail-closed 空集）。 */
    "routing_snapshot_unavailable",
    /** pi 会话没解析出 route（个人订阅腿本来就没有；网关腿 = 清单未加载或该模型无原生 pi 接口，首轮会明确报错）。 */
    "pi_route_unresolved",
    // ③ tools
    /** 声明为必需的连接器里，有名字没能挂成 MCP server。 */
    "required_connector_unmounted",
    /** 声明「至少要有一个连接器」，但一个都没挂上。 */
    "required_connector_none_mounted",
    /** 声明为必需的具名 Agent 不在本会话名册里——跑起来会静默退回默认人格。 */
    "required_agent_unresolved",
    // ④ permission
    /** 这个对话存的权限模式不是当前认得的取值，已被应用默认值顶替（可能悄悄放宽了权限）。 */
    "permission_mode_replaced",
    /** 没解析出生效的权限模式。 */
    "permission_mode_unresolved",
    // ⑤ history — 跨族 resume 按摘要带过历史，不再有 fail 原因码。
];
/**
 * 修复动作码（同样是机器码，UI 按码取 locale + 决定按钮往哪跳）。
 * 「告诉用户哪儿坏了」不够——本卡的产品意义是同时告诉他**下一步该点哪儿**。
 */
export const SESSION_PREFLIGHT_RECOVERY_HINTS = [
    "open_model_settings",
    "open_connector_settings",
    "open_permission_settings",
    "start_new_chat",
    "wait_and_retry",
];
/**
 * 唯一 typed builder。传入的面可以缺、可以乱序——builder 负责补齐成规范的 5 条并算
 * `blocked`，所以生产侧不可能手搓出一份「顺序不对 / 少一面 / blocked 与内容对不上」的报告。
 * 未给出的面按 `unknown` 补齐（诚实分级：没核就是没核，不能记成 pass）。
 */
export function buildSessionPreflightReport(faces) {
    const byFace = new Map();
    for (const result of faces) {
        // 同一面重复给值：后者胜。生产侧不该这么用，但报告结构必须是确定的。
        byFace.set(result.face, result);
    }
    const normalized = SESSION_PREFLIGHT_FACES.map((face) => {
        const found = byFace.get(face);
        if (!found)
            return { face, status: "unknown" };
        if (found.status === "pass") {
            // pass 面不带原因/修复动作——留着它们只会让 UI 渲染出「一切正常，请去设置修复」。
            return { face, status: "pass" };
        }
        return {
            face,
            status: found.status,
            ...(found.reason ? { reason: found.reason } : {}),
            ...(found.detail ? { detail: found.detail } : {}),
            ...(found.recoveryHint ? { recoveryHint: found.recoveryHint } : {}),
        };
    });
    return {
        contractVersion: SESSION_PREFLIGHT_CONTRACT_VERSION,
        blocked: normalized.some((result) => result.status === "fail"),
        faces: normalized,
    };
}
/** 只读取，不重算——给已成型的报告问「这一面怎么样」。 */
export function findSessionPreflightFace(report, face) {
    return report.faces.find((result) => result.face === face);
}
const REPORT_FIELDS = new Set(["contractVersion", "blocked", "faces"]);
const FACE_FIELDS = new Set(["face", "status", "reason", "detail", "recoveryHint"]);
const STATUS_SET = new Set(SESSION_PREFLIGHT_STATUS_VALUES);
const REASON_SET = new Set(SESSION_PREFLIGHT_REASONS);
const RECOVERY_HINT_SET = new Set(SESSION_PREFLIGHT_RECOVERY_HINTS);
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function addUnknownFields(issues, value, allowed, prefix) {
    for (const key of Object.keys(value)) {
        if (allowed.has(key))
            continue;
        const path = prefix ? `${prefix}.${key}` : key;
        issues.push({ path, code: "unknown_field", message: `Unknown field: ${path}` });
    }
}
function optionalEnum(issues, value, field, allowed, path) {
    if (!Object.hasOwn(value, field))
        return;
    const raw = value[field];
    if (typeof raw !== "string") {
        issues.push({ path, code: "invalid_type", message: `${path} must be a string` });
        return;
    }
    if (!allowed.has(raw)) {
        issues.push({ path, code: "invalid_value", message: `${path} has an unregistered value` });
    }
}
/**
 * 严格运行时校验（红线 #7：解析外部输入先 guard，不用 `as` 代替校验）。
 *
 * 报告会穿 IPC 到 renderer 再进阻断态 UI；一份畸形载荷不能有机会渲染成「看起来像结论」
 * 的东西。未知字段一律 fail closed，`blocked` 与 `faces` 不自洽也算畸形——否则一份
 * 「blocked=false 但有一面 fail」的载荷会静默放行本该被拦的发送。
 */
export function validateSessionPreflightReport(value) {
    if (!isRecord(value)) {
        return [{ path: "$", code: "invalid_type", message: "Preflight report must be an object" }];
    }
    const issues = [];
    addUnknownFields(issues, value, REPORT_FIELDS, "");
    if (!Object.hasOwn(value, "contractVersion")) {
        issues.push({
            path: "contractVersion",
            code: "missing_field",
            message: "Missing field: contractVersion",
        });
    }
    else if (value.contractVersion !== SESSION_PREFLIGHT_CONTRACT_VERSION) {
        issues.push({
            path: "contractVersion",
            code: "invalid_value",
            message: `contractVersion must be ${SESSION_PREFLIGHT_CONTRACT_VERSION}`,
        });
    }
    if (!Object.hasOwn(value, "blocked")) {
        issues.push({ path: "blocked", code: "missing_field", message: "Missing field: blocked" });
    }
    else if (typeof value.blocked !== "boolean") {
        issues.push({ path: "blocked", code: "invalid_type", message: "blocked must be a boolean" });
    }
    if (!Object.hasOwn(value, "faces")) {
        issues.push({ path: "faces", code: "missing_field", message: "Missing field: faces" });
        return issues;
    }
    if (!Array.isArray(value.faces)) {
        issues.push({ path: "faces", code: "invalid_type", message: "faces must be an array" });
        return issues;
    }
    if (value.faces.length !== SESSION_PREFLIGHT_FACES.length) {
        issues.push({
            path: "faces",
            code: "invalid_value",
            message: `faces must carry all ${SESSION_PREFLIGHT_FACES.length} faces`,
        });
    }
    let sawFail = false;
    value.faces.forEach((raw, index) => {
        const path = `faces[${index}]`;
        if (!isRecord(raw)) {
            issues.push({ path, code: "invalid_type", message: `${path} must be an object` });
            return;
        }
        addUnknownFields(issues, raw, FACE_FIELDS, path);
        const expectedFace = SESSION_PREFLIGHT_FACES[index];
        if (raw.face !== expectedFace) {
            issues.push({
                path: `${path}.face`,
                code: "invalid_value",
                message: `${path}.face must be ${String(expectedFace)}`,
            });
        }
        if (typeof raw.status !== "string" || !STATUS_SET.has(raw.status)) {
            issues.push({
                path: `${path}.status`,
                code: "invalid_value",
                message: `${path}.status must be one of ${SESSION_PREFLIGHT_STATUS_VALUES.join("|")}`,
            });
        }
        else if (raw.status === "fail") {
            sawFail = true;
        }
        optionalEnum(issues, raw, "reason", REASON_SET, `${path}.reason`);
        optionalEnum(issues, raw, "recoveryHint", RECOVERY_HINT_SET, `${path}.recoveryHint`);
        if (Object.hasOwn(raw, "detail") && typeof raw.detail !== "string") {
            issues.push({
                path: `${path}.detail`,
                code: "invalid_type",
                message: `${path}.detail must be a string`,
            });
        }
    });
    if (typeof value.blocked === "boolean" && value.blocked !== sawFail) {
        issues.push({
            path: "blocked",
            code: "invalid_value",
            message: "blocked must equal whether any face failed",
        });
    }
    return issues;
}
export function isSessionPreflightReport(value) {
    return validateSessionPreflightReport(value).length === 0;
}
//# sourceMappingURL=session-preflight.js.map